import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { all_routes } from '../../router/all_routes';

interface ServiceItem {
    label: string;
    route: string;
    disabled?: boolean;
}

interface ServiceCategory {
    id: string;
    title: string;
    description: string;
    icon: string;
    items: ServiceItem[];
}

const Services = () => {
    const routes = all_routes;
    const [searchQuery, setSearchQuery] = useState('');

    const serviceCategories: ServiceCategory[] = [
        {
            id: 'id-management',
            title: 'Employee Data & Identity Management',
            description: 'Apply for fresh identity cards, manage dependent inclusions and deletions.',
            icon: 'ti ti-id',
            items: [
                {
                    label: 'Application for new I-Card',
                    route: routes.idCard.list
                },
                {
                    label: 'Inclusion/Deletion of dependent',
                    route: routes.idCard.createFamilyMemberRequest
                },
                {
                    label: 'Inclusion & Deletion List',
                    route: routes.idCard.inclusionOfDependent
                }
            ]
        },
        {
            id: 'it-sap-services',
            title: 'IT & SAP Access Management',
            description: 'Apply for SAP roles, server authorization, T-Codes and IT asset requisitions.',
            icon: 'ti ti-lock-access',
            items: [
                {
                    label: 'SAP Authorization Request',
                    route: routes.sapAuthorization.list
                },
                {
                    label: 'New SAP Authorization',
                    route: routes.sapAuthorization.create
                },
                {
                    label: 'E-Requisition (IT Equipment)',
                    route: routes.eRequisitionIT.list
                }
            ]
        },
        {
            id: 'leave-attendance',
            title: 'Leave & Attendance',
            description: 'Apply for leaves, check attendance status and manage shift regularizations.',
            icon: 'ti ti-calendar-user',
            items: [
                {
                    label: 'Leave Applications',
                    route: '#'
                },
                {
                    label: 'Attendance Regularization',
                    route: '#'
                }
            ]
        },
        {
            id: 'medical-leave-encashment',
            title: 'Medical & Leave Encashment',
            description: 'Apply for leave encashment of encashable EL, and IVF / Para 6.2 / Para 6.3 prior approval.',
            icon: 'ti ti-heartbeat',
            items: [
                {
                    label: 'Leave Encashment for EL beyond 60 leave',
                    route: routes.leaveEncashment.list
                },
                {
                    label: 'Prior Approval for IVF, Para 6.2/6.3',
                    route: routes.medicalAttendance.list
                }
            ]
        },
        {
            id: 'health-welfare',
            title: 'Employee Health & Welfare Applications',
            description: 'Apply for annual comprehensive health checkup authority letter and related welfare requests.',
            icon: 'ti ti-heart',
            items: [
                {
                    label: 'Authority Letter for Annual Comprehensive Health Checkup',
                    route: routes.authorityLetterHealthCheckup.list
                },
                {
                    label: 'Staff Welfare Fund Grants',
                    route: routes.staffWelfareFund.list
                }
            ]
        },
        {
            id: 'tax-loan',
            title: 'Tax & Loan Management',
            description: 'View income tax card details and check loan surety received or given status.',
            icon: 'ti ti-receipt-tax',
            items: [
                {
                    label: 'Display Tax Card',
                    route: routes.displayTaxCard
                },
                {
                    label: 'Tax Simplifier Statement',
                    route: routes.taxSimplifier
                },
                {
                    label: 'Loan Surety Status Check',
                    route: routes.loanSurety
                }
            ]
        },
        {
            id: 'financial-retirement',
            title: 'Financial & Retirement Benefits',
            description: 'View yearly NPS/superannuation contributions and annual PF ledger details.',
            icon: 'ti ti-coin',
            items: [
                {
                    label: 'Annual NPS/Superannuation Detail',
                    route: routes.annualNps
                },
                {
                    label: 'Annual PF Details',
                    route: routes.annualPf
                }
            ]
        },
        {
            id: 'transfer-posting',
            title: 'Employee Transfer & Posting Module',
            description: 'Self Transfer applications and cadre movement.',
            icon: 'ti ti-arrows-transfer-down',
            items: [
                {
                    label: 'Self Transfer Applications',
                    route: routes.selfTransfer.list
                },
                {
                    label: 'Internal Vacancy (DMRC/Outstation/Vigilance/Misc)',
                    route: routes.transferPosting.internalVacancy,
                    disabled: true
                },
                {
                    label: 'Mutual Transfer Applications',
                    route: routes.transferPosting.mutualTransfer,
                    disabled: true
                },
                {
                    label: 'Transfer Cancellation Representations',
                    route: routes.transferPosting.cancellation,
                    disabled: true
                },
                {
                    label: 'Request for Posting',
                    route: routes.transferPosting.requestPosting,
                    disabled: true
                },
                {
                    label: 'Download Office Order of Transfer',
                    route: routes.transferPosting.officeOrders,
                    disabled: true
                }
            ]
        },
        {
            id: 'other-hr',
            title: 'Other HR Services',
            description: 'Annual Property Return, VPF contribution, appeal for contribution, and E-Attestation process.',
            icon: 'ti ti-heart-handshake',
            items: [
                {
                    label: 'Annual Property Return',
                    route: routes.annualPropertyReturn
                },
                {
                    label: 'VPF Contribution Module',
                    route: routes.vpfModule.list
                },
                {
                    label: 'Contribution On Appeal',
                    route: routes.appealContribution.list
                },
                {
                    label: 'E-Attestation Process',
                    route: routes.eAttestation.list
                },
                {
                    label: 'Booking of Community Hall/Common Room/Park',
                    route: routes.communityHall.list
                },
                {
                    label: 'Change Password',
                    route: routes.changePassword
                }
            ]
        }
    ];

    const filteredCategories = serviceCategories
        .map((category) => {
            const query = searchQuery.toLowerCase().trim();
            if (!query) return category;

            const matchesCategory =
                category.title.toLowerCase().includes(query) ||
                category.description.toLowerCase().includes(query);

            const filteredItems = category.items.filter((item) =>
                item.label.toLowerCase().includes(query)
            );

            if (matchesCategory) {
                return category;
            }
            if (filteredItems.length > 0) {
                return {
                    ...category,
                    items: filteredItems,
                };
            }
            return null;
        })
        .filter(Boolean) as ServiceCategory[];

    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Page Header */}
                <div className="d-md-flex d-block align-items-center justify-content-between mb-4">
                    <div className="my-auto mb-2">
                        <h3 className="page-title mb-1">Employee Services</h3>
                        <nav>
                            <ol className="breadcrumb mb-0">
                                <li className="breadcrumb-item">
                                    <Link to={routes.adminDashboard}>Dashboard</Link>
                                </li>
                                <li className="breadcrumb-item active" aria-current="page">
                                    All Services
                                </li>
                            </ol>
                        </nav>
                    </div>
                    {/* Search Bar */}
                    <div className="d-flex align-items-center mt-md-0 mt-2" style={{ maxWidth: "320px", width: "100%" }}>
                        <div className="input-group">
                            <span className="input-group-text bg-white border-end-0">
                                <i className="ti ti-search text-muted"></i>
                            </span>
                            <input
                                type="text"
                                className="form-control border-start-0 ps-0"
                                placeholder="Search services..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            {searchQuery && (
                                <button
                                    className="btn btn-outline-secondary border-start-0"
                                    type="button"
                                    onClick={() => setSearchQuery('')}
                                >
                                    <i className="ti ti-x"></i>
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <div className="row g-4">
                    {filteredCategories.length > 0 ? (
                        filteredCategories.map((category) => (
                            <div key={category.id} className="col-xl-6 col-md-12">
                                <div className="card border-0 shadow-sm h-100 overflow-hidden">
                                    <div className="card-header bg-white border-0 pt-4 px-4 pb-0">
                                        <div className="d-flex align-items-center gap-3">
                                            <div className="avatar avatar-lg bg-danger-transparent rounded-3">
                                                <i className={`${category.icon} fs-24 text-danger`}></i>
                                            </div>
                                            <div>
                                                <h5 className="card-title mb-1 fw-bold">{category.title}</h5>
                                                <p className="small text-muted mb-0">{category.description}</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="card-body p-4 pt-4">
                                        <div className="row g-2">
                                            {category.items.map((item, idx) => (
                                                <div key={idx} className="col-md-6">
                                                    <Link 
                                                        to={item.disabled ? "#" : item.route} 
                                                        style={item.disabled ? { opacity: 0.55, cursor: "not-allowed" } : undefined}
                                                        onClick={(e) => {
                                                            if (item.disabled) {
                                                                e.preventDefault();
                                                            }
                                                        }}
                                                        className={`d-flex justify-content-between align-items-center p-3 rounded-3 bg-light text-decoration-none transition-all ${item.disabled ? "text-muted" : "hover-white-shadow border border-transparent hover-border-danger"}`}
                                                    >
                                                        <div className="d-flex align-items-center gap-2">
                                                            <span className="small fw-semibold text-dark">{item.label}</span>
                                                            {item.disabled && (
                                                                <span className="badge bg-white text-muted border fs-10 py-0.5 px-1.5" style={{ fontSize: "10px" }}>
                                                                    Disabled
                                                                </span>
                                                            )}
                                                        </div>
                                                        <i className={`ti ${item.disabled ? "ti-ban text-muted" : "ti-chevron-right text-muted"} fs-12`}></i>
                                                    </Link>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="col-12 text-center py-5">
                            <i className="ti ti-search-off fs-40 text-muted mb-2"></i>
                            <h5 className="text-muted">No services found matching "{searchQuery}"</h5>
                        </div>
                    )}
                </div>
            </div>

            <style>{`
                .hover-white-shadow:hover {
                    background-color: #fff !important;
                    box-shadow: 0 4px 15px rgba(0,0,0,0.05);
                }
                .hover-border-danger:hover {
                    border-color: #A93226 !important;
                }
                .bg-danger-transparent { background-color: rgba(169, 50, 38, 0.1); }
                .transition-all { transition: all 0.25s ease-in-out; }
                .fs-12 { font-size: 12px; }
            `}</style>
        </div>
    );
};

export default Services;
